/**
 * ClientPicker — labelled `<select>` for picking the client a project
 * bills against. Used by ProjectCreateModal + ProjectEditModal.
 *
 * MOTE-143 — the tenant's auto-created internal client always renders
 * **first** as `Internal ({companyName})` with an "Internal" tag, then
 * a thin divider, then every active regular client alphabetical by
 * displayName. Archived clients are hidden. The legacy parity is in
 * `3mote-fe/src/components/CreateProject/CreateProject.jsx` — same
 * label format, same first-position rule, same divider.
 *
 * The picker is intentionally a plain native `<select>` for now (the
 * codebase doesn't ship a reusable styled Combobox yet); the dividing
 * row is a single `disabled` option line — the visual cue is "below
 * this divider is everyone else."
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useClients } from '@/features/tenant-settings/use-clients'

interface ClientPickerProps {
  id: string
  label: string
  value: string | null
  onChange: (next: string | null) => void
  disabled?: boolean
  /** When provided, renders as the help/error text under the select. */
  hint?: string
  /**
   * When true the "No client" placeholder option is hidden and the
   * native browser validation prompts the user to pick one. The label
   * also picks up a red asterisk via the underlying styles.
   */
  required?: boolean
  /** Surfaced under the select as a red error message when set. */
  error?: string | null
}

export function ClientPicker({
  id,
  label,
  value,
  onChange,
  disabled,
  hint,
  required,
  error,
}: ClientPickerProps) {
  const { t } = useTranslation('common')
  const clientsQuery = useClients()

  const { internal, regular } = useMemo(() => {
    const all = (clientsQuery.data ?? []).filter((c) => c.archivedAt === null)
    const internal = all.find((c) => c.isInternal) ?? null
    const regular = all
      .filter((c) => !c.isInternal)
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
    return { internal, regular }
  }, [clientsQuery.data])

  const internalLabel = internal
    ? // Mirrors legacy: `Internal (Aeeiee Inc.)` — falls back to displayName
      // when the org has no `companyName` yet (e.g. brand-new tenant before
      // the user fills in My Organization).
      `${t('clientPicker.internalPrefix', { defaultValue: 'Internal' })} (${
        internal.companyName ?? internal.displayName
      })`
    : null

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-fg">
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      <select
        id={id}
        value={value ?? ''}
        onChange={(event) => {
          const next = event.target.value
          onChange(next === '' ? null : next)
        }}
        disabled={disabled || clientsQuery.isLoading}
        required={required}
        aria-required={required ? 'true' : undefined}
        aria-invalid={error ? 'true' : undefined}
        className="block w-full rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:cursor-not-allowed disabled:opacity-60"
      >
        <option value="" disabled={required}>
          {required
            ? t('clientPicker.pickOne', { defaultValue: 'Pick a client…' })
            : t('clientPicker.unassigned', { defaultValue: 'No client' })}
        </option>
        {internal && internalLabel && <option value={internal.id}>{internalLabel}</option>}
        {internal && regular.length > 0 && (
          // Visual divider — disabled option, can't be selected. Uses ───
          // glyphs so the dropdown still renders the row cleanly across
          // browsers (Chrome ignores <hr> inside <select>; Firefox renders
          // it differently).
          <option disabled>──────────</option>
        )}
        {regular.map((client) => {
          // Label shape: "Display name (Company name)" when the two
          // fields differ; bare "Display name" when they match (or
          // when companyName is empty). Without the equality guard,
          // legacy rows that stored the same string in both columns
          // render as e.g. "Google LLC (Google LLC)" — looks like a
          // duplicate to the user even though it's one row.
          const showCompany = client.companyName && client.companyName !== client.displayName
          return (
            <option key={client.id} value={client.id}>
              {showCompany ? `${client.displayName} (${client.companyName})` : client.displayName}
            </option>
          )
        })}
      </select>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-fg-muted">{hint}</p>
      ) : null}
    </div>
  )
}
